import { useCallback, useEffect, useMemo, useState } from "react";

import { useLazyGetFilterByPropertyQuery } from "@/pages/Private/redux/list/list.api";

/** Like the Lead Scoring autocomplete, nothing is searched for a single character. */
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

/** How long a value taken before the suggestions answered waits for the list's spelling. */
export const SPELLING_WAIT_MS = 1000;

/**
 * The list's spelling of a typed value, or the value as typed when the list takes longer than
 * `waitMs` to answer: a chip input waits for the spelling, but not on a slow lookup (PREC-4515).
 */
export const spellingWithin = (
	spellingOf: (value: string) => Promise<string>,
	typed: string,
	waitMs = SPELLING_WAIT_MS
): Promise<string> =>
	Promise.race([
		spellingOf(typed),
		new Promise<string>((resolve) => setTimeout(() => resolve(typed), waitMs)),
	]);

interface FilterPropertySuggestionsOptions {
	/**
	 * The `list/filterByProperty` property the suggestions come from
	 * (`title`, `industry`, `generalCountry`, …). Undefined turns suggesting off.
	 */
	propertyName?: string;
	/** Chips the user already picked — never offered a second time. */
	selected: string[];
	/**
	 * Values the consumer understands that the property itself cannot suggest
	 * (e.g. the "Rest of the world" catch-all of the location rubric).
	 */
	extraOptions?: string[];
}

export interface FilterPropertySuggestions {
	searchValue: string;
	setSearchValue: (value: string) => void;
	options: string[];
	/** Drops the typed text and the open suggestions — after a value was taken. */
	reset: () => void;
	/**
	 * The list's spelling of a typed value, asked for at once rather than after the debounce: a
	 * value taken on Enter or blur before the suggestions answered still gets it (PREC-4515). The
	 * typed value itself when the list has no entry of that name or the lookup fails.
	 */
	spellingOf: (value: string) => Promise<string>;
	/** Whether the suggestions shown are the list's answer for exactly this text. */
	hasAnswered: (value: string) => boolean;
}

/**
 * Debounced suggestions for a chip input, from the same `list/filterByProperty`
 * lists the Lead Scoring editors use. Own debounce rather than `useSearch`: this
 * needs no router, and a chip input has no page/limit to keep in the URL.
 */
export const useFilterPropertySuggestions = ({
	propertyName,
	selected,
	extraOptions,
}: FilterPropertySuggestionsOptions): FilterPropertySuggestions => {
	const [searchValue, setSearchValue] = useState("");
	const [query, setQuery] = useState("");
	const [fetched, setFetched] = useState<string[]>([]);
	const [answeredQuery, setAnsweredQuery] = useState<string | null>(null);

	const [getFilterByProperty] = useLazyGetFilterByPropertyQuery();

	useEffect(() => {
		const timer = setTimeout(() => setQuery(searchValue.trim()), DEBOUNCE_MS);

		return () => clearTimeout(timer);
	}, [searchValue]);

	useEffect(() => {
		if (!propertyName || query.length < MIN_QUERY_LENGTH) {
			setFetched([]);

			return;
		}

		let isCurrent = true;

		const load = async () => {
			try {
				const result = await getFilterByProperty({ propertyName, searchTerm: query }).unwrap();

				if (isCurrent) {
					setFetched((result?.data ?? []).map((entry) => entry.value).filter(Boolean));
					setAnsweredQuery(query);
				}
			} catch {
				// A failed lookup only means no suggestions — the user can still type.
				if (isCurrent) {
					setFetched([]);
					setAnsweredQuery(query);
				}
			}
		};

		void load();

		return () => {
			isCurrent = false;
		};
	}, [getFilterByProperty, propertyName, query]);

	// Extras are matched on what the user has typed rather than the debounced
	// query: they need no request, so waiting for the debounce only feels laggy.
	const options = useMemo(() => {
		const typed = searchValue.trim().toLowerCase();
		const extras = typed
			? (extraOptions ?? []).filter((option) => option.toLowerCase().includes(typed))
			: [];

		return [...new Set([...extras, ...fetched])].filter((option) => !selected.includes(option));
	}, [extraOptions, fetched, searchValue, selected]);

	const reset = useCallback(() => {
		setSearchValue("");
		setQuery("");
		setFetched([]);
		setAnsweredQuery(null);
	}, []);

	const hasAnswered = useCallback(
		(value: string) => answeredQuery?.toLowerCase() === value.trim().toLowerCase(),
		[answeredQuery]
	);

	const spellingOf = useCallback(
		async (value: string): Promise<string> => {
			const typed = value.trim();

			if (!propertyName || typed.length < MIN_QUERY_LENGTH) {
				return typed;
			}

			try {
				const result = await getFilterByProperty(
					{ propertyName, searchTerm: typed },
					true
				).unwrap();
				const listed = (result?.data ?? [])
					.map((entry) => entry.value)
					.find((entry) => entry?.toLowerCase() === typed.toLowerCase());

				return listed ?? typed;
			} catch {
				return typed;
			}
		},
		[getFilterByProperty, propertyName]
	);

	return { searchValue, setSearchValue, options, reset, spellingOf, hasAnswered };
};
